import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { blogApi, BlogPost } from '@/services/apiService';
import { Calendar, ArrowRight, BookOpen } from 'lucide-react';

const BlogLatestSection = () => {
    const [posts, setPosts] = useState<BlogPost[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        blogApi.getAll(3).then(data => {
            setPosts(data);
            setLoading(false);
        }).catch(() => {
            setLoading(false);
        });
    }, []);

    if (loading) return null;
    if (posts.length === 0) return null;

    return (
        <section className="py-16 md:py-24 bg-muted/30 relative overflow-hidden">
            {/* Decorative background element */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-primary/3 blur-[150px] -z-0 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-amber-500/3 blur-[120px] -z-0 pointer-events-none" />

            <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header with badge */}
                <div className="flex flex-col md:flex-row justify-between items-center mb-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
                            <BookOpen className="h-4 w-4" />
                            Blog
                        </span>
                        <h2 className="text-3xl font-display font-bold text-foreground sm:text-4xl lg:text-5xl">
                            Z naszego bloga
                        </h2>
                        <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
                            Wiedza, porady i ciekawostki prosto z Włoch.
                        </p>
                        <div className="mt-4 h-1 w-24 rounded-full bg-primary" />
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                    >
                        <Button asChild variant="outline" className="mt-6 md:mt-0 border-primary text-primary hover:bg-primary/10 rounded-full px-6">
                            <Link to="/blog">
                                Wszystkie wpisy <ArrowRight className="ml-2 h-4 w-4" />
                            </Link>
                        </Button>
                    </motion.div>
                </div>
                
                {/* Blog grid — first post featured larger */}
                <div className="grid gap-8 md:grid-cols-12">
                    {posts.map((post, index) => (
                        <motion.div
                            key={post.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.15, duration: 0.5 }}
                            className={index === 0 ? 'md:col-span-6' : 'md:col-span-3'}
                        >
                            <Card className="overflow-hidden hover:shadow-xl transition-all duration-500 group h-full border-border/50 hover:border-primary/20">
                                <Link to={`/blog/${post.id}`} className="flex flex-col h-full">
                                    {post.image ? (
                                        <div className={`w-full overflow-hidden ${index === 0 ? 'h-64 md:h-72' : 'h-44 md:h-52'}`}>
                                            <img 
                                                src={post.image} 
                                                alt={post.title} 
                                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                            />
                                        </div>
                                    ) : (
                                        <div className={`w-full bg-secondary/50 flex items-center justify-center ${index === 0 ? 'h-64 md:h-72' : 'h-44 md:h-52'}`}>
                                            <span className="text-muted-foreground opacity-50">Włoska Piwnica</span>
                                        </div>
                                    )}
                                    <CardContent className="p-5 md:p-6 flex flex-col flex-1">
                                        <div className="flex items-center text-sm text-muted-foreground mb-3">
                                            <Calendar className="mr-2 h-4 w-4" />
                                            {new Date(post.createdAt.replace(' ', 'T')).toLocaleDateString('pl-PL')}
                                        </div>
                                        <h3 className={`font-bold mb-3 group-hover:text-primary transition-colors ${index === 0 ? 'text-xl md:text-2xl' : 'text-lg'}`}>
                                            {post.title}
                                        </h3>
                                        <p className={`text-muted-foreground flex-1 ${index === 0 ? 'line-clamp-4' : 'line-clamp-2 text-sm'}`}>
                                            {post.excerpt}
                                        </p>
                                        <div className="mt-4 text-primary font-medium flex items-center group-hover:translate-x-1 transition-transform text-sm">
                                            Czytaj dalej <ArrowRight className="ml-1 h-4 w-4" />
                                        </div>
                                    </CardContent>
                                </Link>
                            </Card>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default BlogLatestSection;
